import { getApp, getApps, initializeApp } from "firebase/app";
import {
  Auth,
  OAuthProvider,
  UserCredential,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  indexedDBLocalPersistence,
  inMemoryPersistence,
  initializeAuth,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const firebaseReady = Boolean(
  config.apiKey && config.authDomain && config.projectId && config.appId,
);

let authInstance: Auth | undefined;
export const observerAuth = (): Auth => {
  if (!firebaseReady)
    throw new Error(
      "Account services are being provisioned. Please try again soon.",
    );
  if (authInstance) return authInstance;
  const app = getApps().length ? getApp() : initializeApp(config);
  authInstance = Capacitor.isNativePlatform()
    ? initializeAuth(app, {
        persistence: [
          indexedDBLocalPersistence,
          browserLocalPersistence,
          inMemoryPersistence,
        ],
      })
    : getAuth(app);
  return authInstance;
};

const retainCredential = async (credential: UserCredential) => {
  localStorage.setItem("apiToken", await credential.user.getIdToken());
  localStorage.setItem("observerUserEmail", credential.user.email || "");
  return credential;
};

export const signInWithProvider = async (provider: "google" | "apple") => {
  const auth = observerAuth();
  const selected =
    provider === "google"
      ? new GoogleAuthProvider()
      : new OAuthProvider("apple.com");
  selected.setCustomParameters({ prompt: "select_account" });
  if (!Capacitor.isNativePlatform())
    return retainCredential(await signInWithPopup(auth, selected));
  if (provider === "google") {
    const result = await FirebaseAuthentication.signInWithGoogle();
    const credential = GoogleAuthProvider.credential(
      result.credential?.idToken,
      result.credential?.accessToken,
    );
    return retainCredential(await signInWithCredential(auth, credential));
  }
  const result = await FirebaseAuthentication.signInWithApple();
  if (!result.credential?.idToken)
    throw new Error("Apple sign-in did not return a credential.");
  const credential = (selected as OAuthProvider).credential({
    idToken: result.credential.idToken,
    rawNonce: result.credential.nonce,
  });
  return retainCredential(await signInWithCredential(auth, credential));
};

export const emailAccount = async (
  email: string,
  password: string,
  register: boolean,
) => {
  const auth = observerAuth();
  const credential = register
    ? await createUserWithEmailAndPassword(auth, email, password)
    : await signInWithEmailAndPassword(auth, email, password);
  return retainCredential(credential);
};

export const resetAccountPassword = (email: string) =>
  sendPasswordResetEmail(observerAuth(), email);
